<template>
  <div class="nif-driving-license-data flex-wrap flex-col">
    <p
      v-if="canShowNif"
      class="f2g-title leading-4 font-medium text-lg mb-3"
      :style="{ color: colors.colorOnBackground, paddingTop: '5px' }"
    >
      {{ $t("forms.nif") }}
    </p>
    <span class="flex flex-col lg:flex-row">
      <v-flex
        v-if="canShowData('identificationTypeMandatoryType')"
        class="lg:w-1/3 w-full"
      >
        <v-autocomplete
          class="f2g-input lg:mr-2 custom-placeholer-color"
          v-model="userDocuments.identificationType"
          :outlined="true"
          :items="identificationTypes"
          :item-text="$PageUtils.getNameLower()"
          item-value="code"
          :error-messages="identificationTypeMandatoryTypeErrors"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @input="sendData"
        >
          <template #label>
            {{ $t("documentType")
            }}<RedAsterisk
              v-if="isRequired('identificationTypeMandatoryType')"
            />
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex v-if="canShowData('nifMandatoryType')" class="lg:w-1/3 w-full">
        <v-text-field
          class="f2g-input lg:mr-2 custom-placeholer-color"
          v-model="userDocuments.nif"
          :outlined="true"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          :error-messages="nifErrors"
          @input="sendData"
        >
          <template #label>
            {{ $t("numberDocumentIdentity")
            }}<RedAsterisk v-if="isRequired('nifMandatoryType')" />
          </template>
        </v-text-field>
      </v-flex>
      <v-flex
        v-if="canShowData('nifValidityDateMandatoryType')"
        class="lg:w-1/3 w-full"
      >
        <DatePickerFW
          class=""
          key="nifValidityDate"
          :label="$t('common.validityDate')"
          :clear="null === userDocuments.nifValidityDate"
          :input="userDocuments.nifValidityDate"
          :minDate="DateUtil.currentDateBasicFormat()"
          :outlined="true"
          :errors="validityDateErrors"
          :required="isRequired('nifValidityDateMandatoryType')"
          frameColours
          :colors="colors"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @form="setNifValidityDate"
        />
      </v-flex>
    </span>
    <span
      v-if="
        canShowData('nifPhotosMandatoryType') &&
          userDocuments.nifValidityDate &&
          !showNifPhotoButtons
      "
      class="text-xs block w-full mb-2"
      :style="{ color: colors.colorOnSurfaceMedium }"
    >
      {{ $t("editValidDateToUpdateDocumentation") }}
    </span>
    <span v-if="showNifPhotoButtons" class="flex flex-col lg:flex-row">
      <v-flex class="lg:w-1/2 w-full">
        <input
          type="file"
          style="display: none"
          id="fileUploadNifPhotosMandatoryTypeFront"
          hidden
          accept="image/png, image/jpeg, image/jpg"
          @change="onFilePickedNifFront"
        />
        <div class="flex flex-col">
          <v-img
            v-if="urlNifFront"
            :src="urlNifFront"
            :aspect-ratio="16 / 9"
            max-width="400px"
            class="image rounded-lg mr-2"
          />
          <div class="flex flex-row mt-2">
            <v-btn
              class="f2g-button lg:w-48 w-1/2 mb-4 border-2 rounded-config30"
              outlined
              :loading="loading"
              :color="colors.colorPrimary"
              @click="chooseFiles('nifPhotosMandatoryTypeFront')"
            >
              <v-icon left small class="mb-1"> mdi-camera-plus </v-icon>
              {{ $t("frontPart")
              }}<RedAsterisk v-if="isRequired('nifPhotosMandatoryType')" />
            </v-btn>
            <span
              v-if="userDocuments.nifImgFrontName"
              class="w-1/2 mt-2 ml-2 mr-2 truncate"
              >{{ userDocuments.nifImgFrontName }}
            </span>
          </div>
          <div class="errorTextPhotos">{{ nifImgFrontNameError }}</div>
        </div>
      </v-flex>
      <v-flex class="lg:w-1/2 w-full">
        <input
          type="file"
          style="display: none"
          id="fileUploadNifPhotosMandatoryTypeReverse"
          hidden
          accept="image/png, image/jpeg, image/jpg"
          @change="onFilePickedNifReverse"
        />
        <div class="flex flex-col">
          <v-img
            v-if="urlNifReverse"
            :src="urlNifReverse"
            :aspect-ratio="16 / 9"
            max-width="400px"
            class="image rounded-lg mr-2"
          />
          <div class="flex flex-row mt-2">
            <v-btn
              class="f2g-button lg:w-48 w-1/2 mb-4 border-2 rounded-config30"
              outlined
              :loading="loading"
              :color="colors.colorPrimary"
              @click="chooseFiles('nifPhotosMandatoryTypeReverse')"
            >
              <v-icon left small class="mb-1"> mdi-camera-plus </v-icon>
              {{ $t("backPart")
              }}<RedAsterisk v-if="isRequired('nifPhotosMandatoryType')" />
            </v-btn>
            <span
              v-if="userDocuments.nifImgReverseName"
              class="w-1/2 mt-2 ml-2 mr-2 truncate"
              >{{ userDocuments.nifImgReverseName }}
            </span>
          </div>
          <div class="errorTextPhotos">{{ nifImgReverseNameError }}</div>
        </div>
      </v-flex>
    </span>
    <span
      v-if="showNifPhotoButtons && imageNifErrors.length > 0"
      class="flex flex-col lg:flex-row errorTextPhotos"
    >
      {{ imageNifErrors[0] }}
    </span>
    <p
      v-if="canShowDrivingLicense"
      class="f2g-title leading-4 font-medium text-lg mb-6"
      :style="{ color: colors.colorOnBackground, paddingTop: '5px' }"
    >
      {{ $t("mobile.register.licenseDriving") }}
    </p>
    <span key="drivingLicense" class="flex flex-col lg:flex-row">
      <v-flex
        v-if="canShowData('drivingLicenseMandatoryType')"
        class="lg:w-1/3 w-full"
      >
        <v-autocomplete
          class="f2g-input lg:mr-2 custom-placeholer-color"
          v-model="userDocuments.drivingLicense"
          :outlined="true"
          :items="drivingLicenseOrigins"
          :item-text="$PageUtils.getNameLower()"
          return-object
          :error-messages="drivingLicenseErrors"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @input="sendData"
        >
          <template #label>
            {{ $t("mobile.register.licenseDriving")
            }}<RedAsterisk v-if="isRequired('drivingLicenseMandatoryType')" />
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex
        v-if="
          userDocuments.drivingLicense &&
            userDocuments.drivingLicense.code ===
              $CONSTANTS.DRIVING_LICENSE.TYPE.OBTAINED_IN_COUNTRY
        "
        class="lg:w-1/3 w-full"
      >
        <v-autocomplete
          class="f2g-input lg:mr-2 custom-placeholer-color"
          v-model="userDocuments.drivingLicenseCountry"
          :outlined="true"
          :items="$PageUtils.orderArray(nationalities)"
          :item-text="$PageUtils.getNameLower()"
          return-object
          :error-messages="drivingLicenseCountryErrors"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @input="drivingLicenseCountryChanges"
        >
          <template #label>
            {{ $t("forms.obtainedCountry") }}<RedAsterisk />
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex
        v-if="canShowData('drivingLicenseNumberingMandatoryType')"
        class="lg:w-1/3 w-full"
      >
        <v-text-field
          class="f2g-input lg:mr-2 custom-placeholer-color"
          v-model="userDocuments.drivingLicenseNumbering"
          :outlined="true"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          :error-messages="drivingLicenseNumberingMandatoryTypeErrors"
          @input="sendData"
        >
          <template #label>
            {{ $t("numeration")
            }}<RedAsterisk
              v-if="isRequired('drivingLicenseNumberingMandatoryType')"
            />
          </template>
        </v-text-field>
      </v-flex>
      <v-flex
        v-if="canShowData('drivingValidityDateMandatoryType')"
        class="lg:w-1/2 w-full"
      >
        <DatePickerFW
          class=""
          key="drivingLicenseValidityDate"
          :label="$t('common.validityDate')"
          :clear="null === userDocuments.drivingLicenseValidityDate"
          :input="userDocuments.drivingLicenseValidityDate"
          :minDate="DateUtil.currentDateBasicFormat()"
          :outlined="true"
          :errors="drivingLicenseValidityDateErrors"
          :required="isRequired('drivingValidityDateMandatoryType')"
          frameColours
          :colors="colors"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @form="setDrivingLicenseValidityDate"
        />
      </v-flex>
    </span>
    <span
      v-if="
        canShowData('drivingPhotosMandatoryType') &&
          userDocuments.drivingLicenseValidityDate &&
          !showDrivingPhotoButtons
      "
      class="text-xs block w-full mb-2"
      :style="{ color: colors.colorOnSurfaceMedium }"
    >
      {{ $t("editValidDateToUpdateDocumentation") }}
    </span>
    <span class="flex flex-col lg:flex-row">
      <v-flex v-if="showDrivingPhotoButtons" class="lg:w-1/2 w-full">
        <input
          type="file"
          style="display: none"
          id="fileUploadDrivingPhotosMandatoryTypeFront"
          hidden
          accept="image/png, image/jpeg, image/jpg"
          @change="onFilePickedDLFront"
        />
        <div class="flex flex-col">
          <v-img
            v-if="urlDLFront"
            :src="urlDLFront"
            :aspect-ratio="16 / 9"
            max-width="400px"
            class="image rounded-lg mr-2"
          />
          <div class="flex flex-row mt-2">
            <v-btn
              class="f2g-button lg:w-48 w-1/2 mb-4 border-2 rounded-config30"
              outlined
              :loading="loading"
              :color="colors.colorPrimary"
              @click="chooseFiles('drivingPhotosMandatoryTypeFront')"
            >
              <v-icon left small class="mb-1"> mdi-camera-plus </v-icon>
              {{ $t("frontPart")
              }}<RedAsterisk v-if="isRequired('drivingPhotosMandatoryType')" />
            </v-btn>
            <span
              v-if="userDocuments.drivingLicenseImgFrontName"
              class="w-1/2 mt-2 ml-2 mr-2 truncate"
            >
              {{ userDocuments.drivingLicenseImgFrontName }}
            </span>
          </div>
          <div class="errorTextPhotos">
            {{ drivingLicenseImgFrontNameError }}
          </div>
        </div>
      </v-flex>
      <v-flex v-if="showDrivingPhotoButtons" class="lg:w-1/2 w-full">
        <input
          type="file"
          style="display: none"
          id="fileUploadDrivingPhotosMandatoryTypeReverse"
          hidden
          accept="image/png, image/jpeg, image/jpg"
          @change="onFilePickedDLReverse"
        />
        <div class="flex flex-col">
          <v-img
            v-if="urlDLreverse"
            :src="urlDLreverse"
            :aspect-ratio="16 / 9"
            max-width="400px"
            class="image rounded-lg mr-2"
          />
          <div class="flex flex-row mt-2">
            <v-btn
              class="f2g-button lg:w-48 w-1/2 mb-4 border-2 rounded-config30"
              outlined
              :loading="loading"
              :color="colors.colorPrimary"
              @click="chooseFiles('drivingPhotosMandatoryTypeReverse')"
            >
              <v-icon left small class="mb-1"> mdi-camera-plus </v-icon>
              {{ $t("backPart")
              }}<RedAsterisk v-if="isRequired('drivingPhotosMandatoryType')" />
            </v-btn>
            <span
              v-if="userDocuments.drivingLicenseImgReverseName"
              class="w-1/2 mt-2 ml-2 mr-2 truncate"
              >{{ userDocuments.drivingLicenseImgReverseName }}
            </span>
          </div>
          <div class="errorTextPhotos">
            {{ drivingLicenseImgReverseNameError }}
          </div>
        </div>
      </v-flex>
    </span>
    <span
      v-if="showDrivingPhotoButtons && imageDrivingLicenseErrors.length > 0"
      class="flex flex-col lg:flex-row errorTextPhotos"
    >
      {{ imageDrivingLicenseErrors[0] }}
    </span>
    <div
      v-if="canShowData('expeditionLicenseBMandatoryType')"
      class="flex flex-row justify-start items-center"
    >
      <v-checkbox
        v-model="userDocuments.drivingLicenseCar"
        class="checkboxPadding"
        :color="colors.colorPrimary"
        :dark="colors.colorBackground === '#000000' ? true : false"
        @change="
          () => {
            userDocuments.drivingLicenseCar
              ? (showLicenseCar = true)
              : (showLicenseCar = false);
          }
        "
      >
        <template v-slot:label>
          <div :style="{ color: colors.colorOnBackground }">
            {{ $t("mobile.register.licenseTypeB") }}
          </div>
        </template>
      </v-checkbox>
    </div>
    <div
      v-if="showLicenseCar"
      class="flex flex-row justify-start items-center mt-3"
    >
      <v-flex class="w-full">
        <DatePickerFW
          class=""
          :label="$t('carLicenseIssueDate')"
          :clear="null === userDocuments.drivingLicenseCarIssueDate"
          :maxDate="DateUtil.currentDateBasicFormat()"
          :outlined="true"
          :input="userDocuments.drivingLicenseCarIssueDate"
          :errors="drivingLicenseCarIssueDateErrors"
          :required="isRequired('expeditionLicenseBMandatoryType')"
          frameColours
          :colors="colors"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @form="setDrivingLicenseCarIssueDate"
        />
      </v-flex>
    </div>
    <div
      v-if="canShowData('expeditionLicenseAMandatoryType')"
      class="flex flex-row justify-start items-center"
    >
      <v-checkbox
        class="checkboxPadding"
        v-model="userDocuments.drivingLicenseMotorcycle"
        :color="colors.colorPrimary"
        :dark="colors.colorBackground === '#000000' ? true : false"
        @change="
          () => {
            userDocuments.drivingLicenseMotorcycle
              ? (showLicenseMotorcycle = true)
              : (showLicenseMotorcycle = false);
          }
        "
      >
        <template v-slot:label>
          <div :style="{ color: colors.colorOnBackground }">
            {{ $t("mobile.register.licenseTypeA") }}
          </div>
        </template>
      </v-checkbox>
    </div>
    <div
      v-if="showLicenseMotorcycle"
      class="flex lg:flex-row flex-col justify-start items-center"
    >
      <v-flex class="lg:w-1/2 w-full mt-3 lg:mr-2">
        <DatePickerFW
          class=""
          :label="$t('motorcycleLicenseIssueDate')"
          :clear="null === userDocuments.drivingLicenseMotorcycleIssueDate"
          :maxDate="DateUtil.currentDateBasicFormat()"
          :outlined="true"
          :input="userDocuments.drivingLicenseMotorcycleIssueDate"
          :errors="drivingLicenseMotorcycleIssueDateErrors"
          :required="isRequired('expeditionLicenseAMandatoryType')"
          frameColours
          :colors="colors"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @form="setDrivingLicenseMotorcycleIssueDate"
        />
      </v-flex>
      <v-flex
        v-if="canShowData('licenseRangeMandatoryType')"
        class="lg:w-1/2 w-full lg:mt-3"
      >
        <v-autocomplete
          v-model="userDocuments.licenseHighestRange"
          class="f2g-input custom-placeholer-color"
          :outlined="true"
          :items="carLicenses"
          :error-messages="licenseHighestRangeErrors"
          :style="
            `--custom_color-text:${colors.colorOnSurface}; --custom_color-label:${colors.colorOnSurfaceMedium}`
          "
          :color="colors.colorPrimary"
          :backgroundColor="colors.colorSurface"
          :dark="colors.colorBackground === '#000000' ? true : false"
          @input="sendData"
        >
          <template #label>
            {{ $t("mobile.register.majorRangeLicenseA")
            }}<RedAsterisk v-if="isRequired('licenseRangeMandatoryType')" />
          </template>
        </v-autocomplete>
      </v-flex>
    </div>
  </div>
</template>

<script>
import { requiredIf } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import DateUtil from "@/flit2go-web-core/util/dateUtil";
import DatePickerFW from "@/modules/v1/Components/web-core/date-pickerFW.vue";
export default {
  name: "NifDrivingLicenseData",
  components: {
    DatePickerFW,
    RedAsterisk: () =>
      import("@/flit2go-web-core/components/fields/f2g-red-asterisk"),
  },
  props: {
    nationalities: Array,
    validate: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    userInfo: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      DateUtil,
      userDocuments: {
        nif: null,
        nifValidityDate: null,
        nifImgFront: null,
        nifImgReverse: null,
        nifImgFrontName: null,
        nifImgReverseName: null,
        drivingLicense: null,
        drivingLicenseCountry: null,
        drivingLicenseValidityDate: null,
        drivingLicenseImgFront: null,
        drivingLicenseImgReverse: null,
        drivingLicenseImgFrontName: null,
        drivingLicenseImgReverseName: null,
        drivingLicenseCar: false,
        drivingLicenseCarIssueDate: null,
        drivingLicenseMotorcycle: false,
        drivingLicenseMotorcycleIssueDate: null,
        licenseHighestRange: null,
        identificationType: null,
        drivingLicenseNumbering: null,
      },
      loading: false,
      showLicenseMotorcycle: false,
      showLicenseCar: false,
      drivingLicenseOrigins: [],
      carLicenses: ["A", "A2", "A1", "AM"],
      urlNifFront: null,
      urlNifReverse: null,
      urlDLFront: null,
      urlDLreverse: null,
      identificationTypes: [],
      nifImgFrontNameError: "",
      nifImgReverseNameError: "",
      drivingLicenseImgFrontNameError: "",
      drivingLicenseImgReverseNameError: "",
      nifValidityDateModified: false,
      drivingLicenseValidityDateModified: false,
      initialNifValidityDate: null,
      initialDrivingLicenseValidityDate: null,
    };
  },
  validations: {
    userDocuments: {
      nif: {
        required: requiredIf(function() {
          return this.isRequired("nifMandatoryType");
        }),
      },
      nifValidityDate: {
        required: requiredIf(function() {
          return this.isRequired("nifValidityDateMandatoryType");
        }),
      },
      nifImgFront: {
        required: requiredIf(function() {
          // Not required when a NIF photo is already persisted (validateImg.*).
          return (
            this.isRequired("nifPhotosMandatoryType") &&
            !this.userDocuments.nifImgFrontName &&
            !this.hasNifPhotos
          );
        }),
      },
      nifImgReverse: {
        required: requiredIf(function() {
          // Not required when a NIF photo is already persisted (validateImg.*).
          return (
            this.isRequired("nifPhotosMandatoryType") &&
            !this.userDocuments.nifImgReverseName &&
            !this.hasNifPhotos
          );
        }),
      },
      drivingLicense: {
        required: requiredIf(function() {
          return this.isRequired("drivingLicenseMandatoryType");
        }),
      },
      drivingLicenseImgFront: {
        required: requiredIf(function() {
          // Not required when a license photo is already persisted (validateImg.*).
          return (
            this.isRequired("drivingPhotosMandatoryType") &&
            !this.userDocuments.drivingLicenseImgFrontName &&
            !this.hasDrivingPhotos
          );
        }),
      },
      drivingLicenseImgReverse: {
        required: requiredIf(function() {
          // Not required when a license photo is already persisted (validateImg.*).
          return (
            this.isRequired("drivingPhotosMandatoryType") &&
            !this.userDocuments.drivingLicenseImgReverseName &&
            !this.hasDrivingPhotos
          );
        }),
      },
      drivingLicenseCountry: {
        required: requiredIf(function() {
          return (
            this.userDocuments.drivingLicense &&
            this.userDocuments.drivingLicense.code ===
              this.$CONSTANTS.DRIVING_LICENSE.TYPE.OBTAINED_IN_COUNTRY
          );
        }),
      },
      drivingLicenseValidityDate: {
        required: requiredIf(function() {
          return this.isRequired("drivingValidityDateMandatoryType");
        }),
      },
      drivingLicenseCarIssueDate: {
        required: requiredIf(function() {
          return this.isRequired("expeditionLicenseBMandatoryType");
        }),
      },
      drivingLicenseMotorcycleIssueDate: {
        required: requiredIf(function() {
          return this.isRequired("expeditionLicenseAMandatoryType");
        }),
      },
      licenseHighestRange: {
        required: requiredIf(function() {
          return this.isRequired("licenseRangeMandatoryType");
        }),
      },
      identificationType: {
        required: requiredIf(function() {
          return this.isRequired("identificationTypeMandatoryType");
        }),
      },
      drivingLicenseNumbering: {
        required: requiredIf(function() {
          return this.isRequired("drivingLicenseNumberingMandatoryType");
        }),
      },
    },
  },
  watch: {
    validate() {
      this.$v.$touch();
      if (this.isRequired("expeditionLicenseBMandatoryType")) {
        this.activeLicenseCar();
      }
      if (this.isRequired("expeditionLicenseAMandatoryType")) {
        this.activeLicenseMoto();
      }
      this.$emit("validation", {
        valid: !this.$v.$invalid,
      });
    },
  },
  mounted() {
    if (this.userInfo) {
      this.fillUserInfo();
    }
  },
  computed: {
    ...mapGetters({
      colors: "configuration/getColors",
      language: "configuration/getUserLanguage",
      filterInformationUsers: "configuration/getFilterInformationUsers",
    }),
    hasNifPhotos() {
      // Presence-only boolean of persisted NIF photos. Source of truth is
      // userInfo.validateImg (paths of the already stored images returned by
      // GET secured/v1/frameweb/user). RGPD: never render these URLs.
      return Boolean(
        this.userInfo?.validateImg?.imgDniFront &&
          this.userInfo?.validateImg?.imgDniReverse
      );
    },
    hasDrivingPhotos() {
      // Presence-only boolean of persisted driving license photos. See note in
      // hasNifPhotos. RGPD: never render these URLs.
      return Boolean(
        this.userInfo?.validateImg?.imgDrivingLicenseFront &&
          this.userInfo?.validateImg?.imgDrivingLicenseReverse
      );
    },
    showNifPhotoButtons() {
      return (
        this.canShowData("nifPhotosMandatoryType") &&
        (!this.hasNifPhotos || this.nifValidityDateModified)
      );
    },
    showDrivingPhotoButtons() {
      return (
        this.canShowData("drivingPhotosMandatoryType") &&
        (!this.hasDrivingPhotos || this.drivingLicenseValidityDateModified)
      );
    },
    canShowNif() {
      let show =
        this.canShowData("nifMandatoryType") ||
        this.canShowData("nifValidityDateMandatoryType") ||
        this.canShowData("nifPhotosMandatoryType") ||
        this.canShowData("identificationTypeMandatoryType");
      return show;
    },
    canShowDrivingLicense() {
      let show =
        this.canShowData("drivingLicenseMandatoryType") ||
        this.canShowData("drivingValidityDateMandatoryType") ||
        this.canShowData("drivingPhotosMandatoryType") ||
        this.canShowData("expeditionLicenseBMandatoryType") ||
        this.canShowData("expeditionLicenseAMandatoryType") ||
        this.canShowData("licenseRangeMandatoryType") ||
        this.canShowData("drivingLicenseNumberingMandatoryType");
      return show;
    },
    nifErrors() {
      const errors = [];
      if (this.$v.userDocuments.nif.$dirty) {
        !this.$v.userDocuments.nif.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    validityDateErrors() {
      const errors = [];
      if (this.$v.userDocuments.nifValidityDate.$dirty) {
        !this.$v.userDocuments.nifValidityDate.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    imageNifErrors() {
      const errors = [];
      if (
        this.$v.userDocuments.nifImgFront.$dirty ||
        this.$v.userDocuments.nifImgReverse.$dirty
      ) {
        (!this.$v.userDocuments.nifImgFront.required ||
          !this.$v.userDocuments.nifImgReverse.required) &&
          errors.push(this.$t("documentPhotosRequired"));
      }
      return errors;
    },
    drivingLicenseErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicense.$dirty) {
        !this.$v.userDocuments.drivingLicense.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    imageDrivingLicenseErrors() {
      const errors = [];
      if (
        this.$v.userDocuments.drivingLicenseImgFront.$dirty ||
        this.$v.userDocuments.drivingLicenseImgReverse.$dirty
      ) {
        (!this.$v.userDocuments.drivingLicenseImgFront.required ||
          !this.$v.userDocuments.drivingLicenseImgReverse.required) &&
          errors.push(this.$t("documentPhotosRequired"));
      }
      return errors;
    },
    drivingLicenseCountryErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicenseCountry.$dirty) {
        !this.$v.userDocuments.drivingLicenseCountry.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    drivingLicenseValidityDateErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicenseValidityDate.$dirty) {
        !this.$v.userDocuments.drivingLicenseValidityDate.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    drivingLicenseCarIssueDateErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicenseCarIssueDate.$dirty) {
        !this.$v.userDocuments.drivingLicenseCarIssueDate.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    drivingLicenseMotorcycleIssueDateErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicenseMotorcycleIssueDate.$dirty) {
        !this.$v.userDocuments.drivingLicenseMotorcycleIssueDate.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    licenseHighestRangeErrors() {
      const errors = [];
      if (this.$v.userDocuments.licenseHighestRange.$dirty) {
        !this.$v.userDocuments.licenseHighestRange.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    identificationTypeMandatoryTypeErrors() {
      const errors = [];
      if (this.$v.userDocuments.identificationType.$dirty) {
        !this.$v.userDocuments.identificationType.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
    drivingLicenseNumberingMandatoryTypeErrors() {
      const errors = [];
      if (this.$v.userDocuments.drivingLicenseNumbering.$dirty) {
        !this.$v.userDocuments.drivingLicenseNumbering.required &&
          errors.push(this.$t("errors.required"));
      }
      return errors;
    },
  },
  created() {
    this.getDrivingLicenses();
    this.getIdentificationTypes();
  },
  methods: {
    drivingLicenseCountryChanges() {
      if (
        this.userDocuments.drivingLicenseCountry?.code === "ES" &&
        this.userDocuments.nif?.length > 0
      ) {
        this.userDocuments.drivingLicenseNumbering = this.userDocuments.nif;
      }
      this.sendData();
    },
    sendData() {
      this.$emit("nifDrivingLicenseData", this.userDocuments);
    },
    emitImagePreviews() {
      const previews = {};
      if (this.urlNifFront) previews.imgDniFront = this.urlNifFront;
      if (this.urlNifReverse) previews.imgDniReverse = this.urlNifReverse;
      if (this.urlDLFront) previews.imgDrivingLicenseFront = this.urlDLFront;
      if (this.urlDLreverse) {
        previews.imgDrivingLicenseReverse = this.urlDLreverse;
      }
      if (this.userDocuments.nifImgFrontName) {
        previews.nifImgFrontName = this.userDocuments.nifImgFrontName;
      }
      if (this.userDocuments.nifImgReverseName) {
        previews.nifImgReverseName = this.userDocuments.nifImgReverseName;
      }
      if (this.userDocuments.drivingLicenseImgFrontName) {
        previews.drivingLicenseImgFrontName = this.userDocuments.drivingLicenseImgFrontName;
      }
      if (this.userDocuments.drivingLicenseImgReverseName) {
        previews.drivingLicenseImgReverseName = this.userDocuments.drivingLicenseImgReverseName;
      }
      if (Object.keys(previews).length > 0) {
        this.$emit("image-previews", previews);
      }
    },
    activeLicenseCar() {
      this.userDocuments.drivingLicenseCar = true;
      this.showLicenseCar = true;
    },
    activeLicenseMoto() {
      this.userDocuments.drivingLicenseMotorcycle = true;
      this.showLicenseMotorcycle = true;
    },
    normalizeValidityDate(date) {
      return date ? DateUtil.formatBasicDate(date) : null;
    },
    setDrivingLicenseValidityDate(drivingLicenseValidityDate) {
      this.userDocuments.drivingLicenseValidityDate = drivingLicenseValidityDate;
      this.drivingLicenseValidityDateModified =
        this.normalizeValidityDate(drivingLicenseValidityDate) !==
        this.normalizeValidityDate(this.initialDrivingLicenseValidityDate);
      this.sendData();
    },
    setNifValidityDate(nifValidityDate) {
      this.userDocuments.nifValidityDate = nifValidityDate;
      this.nifValidityDateModified =
        this.normalizeValidityDate(nifValidityDate) !==
        this.normalizeValidityDate(this.initialNifValidityDate);
      this.sendData();
    },
    setDrivingLicenseCarIssueDate(drivingLicenseCarIssueDate) {
      this.userDocuments.drivingLicenseCarIssueDate = drivingLicenseCarIssueDate;
      this.sendData();
    },
    setDrivingLicenseMotorcycleIssueDate(drivingLicenseMotorcycleIssueDate) {
      this.userDocuments.drivingLicenseMotorcycleIssueDate = drivingLicenseMotorcycleIssueDate;
      this.sendData();
    },
    getDrivingLicenses() {
      this.$store.dispatch("user/getDrivingLicensesOrigins").then((resp) => {
        this.drivingLicenseOrigins = resp;
      });
    },
    getIdentificationTypes() {
      this.$store.dispatch("user/getIdentificationTypes").then((resp) => {
        this.identificationTypes = resp;
      });
    },
    isRequired(element) {
      return (
        this.filterInformationUsers[element] ===
        this.$CONSTANTS.USER.MANDATORY.MANDATORY
      );
    },
    isOptional(element) {
      return (
        this.filterInformationUsers[element] ===
        this.$CONSTANTS.USER.MANDATORY.OPTIONAL
      );
    },
    canShowData(element) {
      return (
        this.filterInformationUsers[element] !==
        this.$CONSTANTS.USER.MANDATORY.NOT_APPEAR
      );
    },
    chooseFiles(openInputFile) {
      switch (openInputFile) {
        case "nifPhotosMandatoryTypeFront":
          document
            .getElementById("fileUploadNifPhotosMandatoryTypeFront")
            .click();
          break;
        case "nifPhotosMandatoryTypeReverse":
          document
            .getElementById("fileUploadNifPhotosMandatoryTypeReverse")
            .click();
          break;
        case "drivingPhotosMandatoryTypeFront":
          document
            .getElementById("fileUploadDrivingPhotosMandatoryTypeFront")
            .click();
          break;
        case "drivingPhotosMandatoryTypeReverse":
          document
            .getElementById("fileUploadDrivingPhotosMandatoryTypeReverse")
            .click();
          break;
      }
    },
    onFilePickedNifFront(event) {
      this.nifImgFrontNameError = "";
      const files = event.target.files;
      if (
        files[0].type === "image/jpeg" ||
        files[0].type === "image/jpg" ||
        files[0].type === "image/png"
      ) {
        let filename = files[0].name;
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
          this.urlNifFront = fileReader.result;
          this.emitImagePreviews();
        });
        fileReader.readAsDataURL(files[0]);
        this.userDocuments.nifImgFront = files[0];
        this.userDocuments.nifImgFrontName = filename;
        this.sendData();
      } else {
        this.nifImgFrontNameError = this.$t("fileTypeUploadImgError");
      }
    },
    onFilePickedNifReverse(event) {
      this.nifImgReverseNameError = "";
      const files = event.target.files;
      if (
        files[0].type === "image/jpeg" ||
        files[0].type === "image/jpg" ||
        files[0].type === "image/png"
      ) {
        let filename = files[0].name;
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
          this.urlNifReverse = fileReader.result;
          this.emitImagePreviews();
        });
        fileReader.readAsDataURL(files[0]);
        this.userDocuments.nifImgReverse = files[0];
        this.userDocuments.nifImgReverseName = filename;
        this.sendData();
      } else {
        this.nifImgReverseNameError = this.$t("fileTypeUploadImgError");
      }
    },
    onFilePickedDLFront(event) {
      this.drivingLicenseImgFrontNameError = "";
      const files = event.target.files;
      if (
        files[0].type === "image/jpeg" ||
        files[0].type === "image/jpg" ||
        files[0].type === "image/png"
      ) {
        let filename = files[0].name;
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
          this.urlDLFront = fileReader.result;
          this.emitImagePreviews();
        });
        fileReader.readAsDataURL(files[0]);
        this.userDocuments.drivingLicenseImgFront = files[0];
        this.userDocuments.drivingLicenseImgFrontName = filename;
        this.sendData();
      } else {
        this.drivingLicenseImgFrontNameError = this.$t(
          "fileTypeUploadImgError"
        );
      }
    },
    onFilePickedDLReverse(event) {
      this.drivingLicenseImgReverseNameError = "";
      const files = event.target.files;
      if (
        files[0].type === "image/jpeg" ||
        files[0].type === "image/jpg" ||
        files[0].type === "image/png"
      ) {
        let filename = files[0].name;
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
          this.urlDLreverse = fileReader.result;
          this.emitImagePreviews();
        });
        fileReader.readAsDataURL(files[0]);
        this.userDocuments.drivingLicenseImgReverse = files[0];
        this.userDocuments.drivingLicenseImgReverseName = filename;
        this.sendData();
      } else {
        this.drivingLicenseImgReverseNameError = this.$t(
          "fileTypeUploadImgError"
        );
      }
    },
    fillUserInfo() {
      this.userDocuments.drivingLicenseMotorcycle = this.userInfo
        .expeditionLicenseA
        ? true
        : false;
      this.userDocuments.drivingLicenseCar = this.userInfo.expeditionLicenseB
        ? true
        : false;
      this.showLicenseCar = this.userDocuments.drivingLicenseCar;
      this.showLicenseMotorcycle = this.userDocuments.drivingLicenseMotorcycle;
      setTimeout(() => {
        this.userDocuments.nif = this.userInfo.nif;
        this.userDocuments.nifValidityDate = this.userInfo.nifValidityDate
          ? DateUtil.formatBasicDate(this.userInfo.nifValidityDate)
          : null;
        this.userDocuments.drivingLicense = this.userInfo.typeLicenseDriving;
        this.userDocuments.drivingLicenseCountry = this.userInfo
          .countryLicenseDriving
          ? this.userInfo.countryLicenseDriving
          : null;
        this.userDocuments.drivingLicenseValidityDate = this.userInfo
          .drivingPermissionValidityDate
          ? DateUtil.formatBasicDate(
              this.userInfo.drivingPermissionValidityDate
            )
          : null;
        this.userDocuments.drivingLicenseCarIssueDate = this.userInfo
          .expeditionLicenseB
          ? DateUtil.formatBasicDate(this.userInfo.expeditionLicenseB)
          : null;
        this.userDocuments.drivingLicenseMotorcycleIssueDate = this.userInfo
          .expeditionLicenseA
          ? DateUtil.formatBasicDate(this.userInfo.expeditionLicenseA)
          : null;
        this.userDocuments.licenseHighestRange = this.userInfo.licenseTypeA;
        this.userDocuments.drivingLicenseNumbering = this.userInfo.drivingLicenseNumbering;
        if (this.userInfo.identificationType?.code) {
          this.userDocuments.identificationType = this.userInfo.identificationType.code;
        }
        this.userDocuments.nifImgFrontName =
          this.userInfo.nifImgFrontName || null;
        this.userDocuments.nifImgReverseName =
          this.userInfo.nifImgReverseName || null;
        this.userDocuments.drivingLicenseImgFrontName =
          this.userInfo.drivingLicenseImgFrontName || null;
        this.userDocuments.drivingLicenseImgReverseName =
          this.userInfo.drivingLicenseImgReverseName || null;
        this.initialNifValidityDate = this.userDocuments.nifValidityDate;
        this.initialDrivingLicenseValidityDate = this.userDocuments.drivingLicenseValidityDate;
        this.nifValidityDateModified = false;
        this.drivingLicenseValidityDateModified = false;
        this.$v.$touch();
        this.$emit("validation", {
          valid: !this.$v.$invalid,
        });
      }, 400);
    },
  },
};
</script>
<style lang="scss">
.checkboxPadding > .v-input__control > .v-input__slot {
  padding-left: 0 !important;
}
.errorTextPhotos {
  color: #b71c1c !important;
  font-size: 0.75rem !important;
}
.custom-placeholer-color input {
  color: var(--custom_color-text) !important;
}
.custom-placeholer-color .v-label {
  color: var(--custom_color-label) !important;
  opacity: 1;
}
</style>
